import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Check, Sparkles } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { FaqAccordion, getFaqs } from "@/components/pricing/faq";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/lib/utils";
import { useSubscriptionUpgrade } from "@/hooks/auth/use-subscription-upgrade";
import { useSubscriptions } from "@/hooks/query/billing";
import { useSession } from "@/lib/auth-client";
import { createWebPageJsonLd, seo } from "@/lib/seo";
import { getPlans } from "@/lib/subscriptions/subscriptions";
import * as m from "@/paraglide/messages";

export const Route = createFileRoute("/(landing)/pricing/")({
	component: PricingPage,
	head: ({ match }) => {
		const title = `${m.pricing_heading()} | StudyOS Pricing`;
		const description = m.pricing_subtitle();

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["StudyOS pricing", "AI learning plans", "pricing for students and teams"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

function PricingPage() {
	const [yearly, setYearly] = useState(false);
	const plans = getPlans();
	const faqs = getFaqs();
	const currency = m.pricing_currency();
	const perMonth = m.pricing_perMonth();
	const { data: session } = useSession();
	const isLoggedIn = !!session?.user;
	const navigate = useNavigate();
	const { data: subscriptions } = useSubscriptions();

	const currentPlan = useMemo(() => {
		const activeSubscription = subscriptions?.find(
			(s) => s.status === "active" || s.status === "trialing",
		);
		return activeSubscription?.plan ?? "free";
	}, [subscriptions]);

	const { mutate: upgrade, isPending } = useSubscriptionUpgrade({
		successUrl: "/chat",
		cancelUrl: "/pricing",
	});

	const subscribeToPlan = useCallback(
		(planId: string, annual: boolean) => {
			upgrade({ plan: planId, annual });
		},
		[upgrade],
	);

	return (
		<div className="min-h-screen">
			{/* Hero */}
			<section className="relative flex items-center justify-center overflow-hidden px-4 pt-24 pb-16 md:pt-32 md:pb-20">
				<div className="absolute inset-0 overflow-hidden" aria-hidden="true">
					<div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
					<div className="absolute right-1/4 bottom-0 h-80 w-80 rounded-full bg-accent/60 blur-3xl" />
				</div>

				<div className="relative z-10 mx-auto max-w-3xl text-center">
					<div
						className="mb-6 flex justify-center opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out forwards",
						}}
					>
						<div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-accent px-4 py-1.5 text-sm text-accent-foreground">
							<Sparkles className="h-4 w-4 text-primary" />
							<span>{m.pricing_badge()}</span>
						</div>
					</div>

					<h1
						className="mb-4 text-4xl font-bold tracking-tight text-foreground opacity-0 md:text-5xl"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.1s forwards",
						}}
					>
						{m.pricing_heading()}
					</h1>

					<p
						className="mx-auto mb-10 max-w-xl text-lg leading-relaxed text-muted-foreground opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.2s forwards",
						}}
					>
						{m.pricing_subtitle()}
					</p>

					{/* Billing toggle */}
					<div
						className="flex items-center justify-center gap-3 opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.25s forwards",
						}}
					>
						<div className="inline-flex items-center rounded-full bg-muted p-1">
							<button
								type="button"
								className={cn(
									"rounded-full px-5 py-1.5 text-sm font-medium transition-all duration-200",
									!yearly
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground",
								)}
								onClick={() => setYearly(false)}
							>
								{m.pricing_monthly()}
							</button>
							<button
								type="button"
								className={cn(
									"rounded-full px-5 py-1.5 text-sm font-medium transition-all duration-200",
									yearly
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground",
								)}
								onClick={() => setYearly(true)}
							>
								{m.pricing_yearly()}
							</button>
						</div>
						{yearly && (
							<span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
								{m.pricing_yearlySave()}
							</span>
						)}
					</div>
				</div>
			</section>

			{/* Pricing cards */}
			<section className="px-4 pb-16 md:pb-24">
				<div
					className="mx-auto grid max-w-5xl gap-6 opacity-0 md:grid-cols-3"
					style={{
						animation: "fade-in-up 0.6s ease-out 0.35s forwards",
					}}
				>
					{plans.map((plan) => {
						const price = yearly ? plan.yearlyPrice : plan.price;
						const isFree = price === "$0";
						const isCurrentPlan = currentPlan === plan.id;
						const displayPrice = price ?? "$0";

						return (
							<div
								key={plan.name}
								className={cn(
									"relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg",
									plan.highlighted
										? "border-primary/50 bg-background shadow-md"
										: "border-border bg-background",
								)}
							>
								{plan.popular && (
									<div className="absolute -top-3 right-6">
										<span className="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
											{m.pricing_proPopular()}
										</span>
									</div>
								)}

								<div className="mb-6">
									<h3 className="mb-1 text-lg font-semibold text-foreground">
										{plan.name}
									</h3>
									<p className="text-sm text-muted-foreground">
										{plan.description}
									</p>
								</div>

								<div className="mb-6">
									<div className="flex items-baseline gap-1">
										{!isFree && (
											<span className="text-lg font-medium text-muted-foreground">
												{currency}
											</span>
										)}
										<span className="text-4xl font-bold tracking-tight text-foreground">
											{isFree
												? currency + displayPrice.replace("$", "")
												: displayPrice.replace("$", "")}
										</span>
										{!isFree && (
											<span className="text-sm text-muted-foreground">
												{perMonth}
											</span>
										)}
									</div>
								</div>

								<ul className="mb-8 flex-1 space-y-3">
									{plan.features.map((feature) => (
										<li
											key={feature}
											className="flex items-start gap-2.5 text-sm text-muted-foreground"
										>
											<Check className="mt-0.5 size-4 shrink-0 text-primary" />
											<span>{feature}</span>
										</li>
									))}
								</ul>

								{isCurrentPlan ? (
									<Button
										className={cn(
											"w-full rounded-lg py-2.5 text-sm font-medium",
											plan.highlighted
												? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md"
												: "border border-border bg-background text-foreground hover:bg-muted",
										)}
										variant={plan.highlighted ? "default" : "outline"}
										disabled
									>
										{m.subscription_currentPlan()}
									</Button>
								) : plan.id === "free" ? (
									<Link to="/chat">
										<Button
											className={cn(
												"w-full rounded-lg py-2.5 text-sm font-medium",
												plan.highlighted
													? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md"
													: "border border-border bg-background text-foreground hover:bg-muted",
											)}
											variant={plan.highlighted ? "default" : "outline"}
										>
											{plan.cta}
										</Button>
									</Link>
								) : (
									<Button
										className={cn(
											"w-full rounded-lg py-2.5 text-sm font-medium",
											plan.highlighted
												? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md"
												: "border border-border bg-background text-foreground hover:bg-muted",
										)}
										variant={plan.highlighted ? "default" : "outline"}
										disabled={isPending}
										onClick={() => {
											if (isLoggedIn) {
												subscribeToPlan(plan.id, yearly);
											} else {
												navigate({ to: "/sign-up" });
											}
										}}
									>
										{isPending ? m.subscription_redirecting() : plan.cta}
									</Button>
								)}
							</div>
						);
					})}
				</div>
			</section>

			{/* FAQ */}
			<section className="px-4 pb-16 md:pb-24">
				<div className="mx-auto max-w-2xl">
					<h2
						className="mb-8 text-center text-2xl font-bold tracking-tight text-foreground opacity-0 md:text-3xl"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.5s forwards",
						}}
					>
						{m.pricing_faqTitle()}
					</h2>
					<div
						className="rounded-2xl border border-border bg-background p-6 opacity-0"
						style={{
							animation: "fade-in-up 0.6s ease-out 0.55s forwards",
						}}
					>
						<FaqAccordion faqs={faqs} />
					</div>
				</div>
			</section>
		</div>
	);
}
